import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type ParameterSet } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';
import type { ParameterSetRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const PARAMETER_SETS = 'parameterSets';

const converter = buildConverter<ParameterSet>(
  (data) =>
    ({
      ...data,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
      lastUsedAt: toTimestampString(data.lastUsedAt) ?? undefined,
    }) as ParameterSet,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreParameterSetRepository implements ParameterSetRepository {
  async findAll(): Promise<ParameterSet[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, PARAMETER_SETS),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('updatedAt', 'desc'),
      limit(200)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<ParameterSet | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, PARAMETER_SETS, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<ParameterSet[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, PARAMETER_SETS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('updatedAt', 'desc'),
      limit(200)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(parameterSet: ParameterSet): Promise<ParameterSet> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const data = { ...parameterSet, environment: getCurrentEnvironment() };
    const existing = await this.findById(parameterSet.id);
    await setDoc(doc(db, PARAMETER_SETS, parameterSet.id).withConverter(converter), data);
    return { ...data, createdAt: existing?.createdAt ?? parameterSet.createdAt, updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, PARAMETER_SETS, id));
  }

  async duplicate(id: string, newName?: string): Promise<ParameterSet | undefined> {
    const original = await this.findById(id);
    if (!original) return undefined;
    const duplicated: ParameterSet = {
      ...original,
      id: `paramset-${Date.now()}`,
      name: newName ?? `${original.name} (Copy)`,
      parentId: original.id,
      revision: 1,
      updatedAt: new Date().toISOString(),
    };
    return this.save(duplicated);
  }
}

export const createFirestoreParameterSetRepository = (): FirestoreParameterSetRepository =>
  new FirestoreParameterSetRepository();
